// src/components/layouts/app/footer/index.tsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  ArrowUp,
  Facebook,
  Instagram,
  Linkedin,
  Youtube,
  Smartphone,
  Shield,
  Truck,
  CheckCircle,
  HelpCircle,
  CreditCard,
  ShoppingBag,
  Store,
  Users,
  ChevronRight,
  MessageCircle,
  Calendar,
  Sun,
  Moon
} from "lucide-react";
import "animate.css";

// Données par défaut (à remplacer par vos données réelles)
const defaultSettings = {
  company_name: "Kayovex",
  email: "contact@kayovex.com",
  phone: "+229 01 57 52 56 06",
  address: "Cotonou, Bénin",
  short_description:
    "La marketplace panafricaine qui connecte acheteurs et vendeurs. Produits de qualité, livraison rapide et paiement sécurisé.",
  social_links: {
    facebook: "https://facebook.com/kayovex",
    instagram: "https://instagram.com/kayovex",
    linkedin: "https://linkedin.com/company/kayovex",
    youtube: "https://youtube.com/@kayovex",
    whatsapp: "https://wa.me/2290123456789",
  },

};

// Composant FooterComponent
const FooterComponent: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const footerRef = useRef<HTMLElement>(null);
  const [showButton, setShowButton] = useState(false);
  const [footerAnimation, setFooterAnimation] = useState(false);
  const [buttonAnimation, setButtonAnimation] = useState(false);
  const scrollAnimationId = useRef<number | null>(null);

  // Récupération des données – à adapter avec votre vrai contexte (ex: usePublic)
  // Ici on utilise les valeurs par défaut, mais vous pouvez les remplacer par vos propres données.
  const settings = defaultSettings; // Remplacez par votre vrai contexte


  // Liens utiles – regroupe les sections du footer (version desktop)
  const linkSections = [
    {
      title: "Aide & Support",
      icon: HelpCircle,
      links: [
        { label: "Centre d'aide", path: "/help-center" },
        { label: "Livraison", path: "/delivery" },
        { label: "Retours et remboursements", path: "/refunds" },
        { label: "FAQ", path: "/faq" },
      ],
    },
    {
      title: "Acheter",
      icon: ShoppingBag,
      links: [
        { label: "Toutes les catégories", path: "/categories" },
        { label: "Promotions", path: "/deals" },
        { label: "Nouveautés", path: "/new" },
        { label: "Marques", path: "/brands" },
      ],
    },
    {
      title: "Vendre",
      icon: Store,
      links: [
        { label: "Devenir vendeur", path: "/become-seller", highlight: true },
        { label: "Espace vendeur", path: "/seller" },
        { label: "Tarifs", path: "/seller-fees" },
      ],
    },
    {
      title: "Kayovex",
      icon: Users,
      links: [
        { label: "À propos", path: "/about" },
        { label: "Blog", path: "/blog" },
        { label: "Carrières", path: "/careers" },
        { label: "Contact", path: "/contact" },
      ],
    },
  ];

  // Réseaux sociaux
  const socialLinks = [
    { icon: Facebook, href: settings.social_links.facebook, label: "Facebook", color: "hover:bg-blue-100 hover:text-blue-600" },
    { icon: Instagram, href: settings.social_links.instagram, label: "Instagram", color: "hover:bg-pink-100 hover:text-pink-600" },
    { icon: Linkedin, href: settings.social_links.linkedin, label: "LinkedIn", color: "hover:bg-blue-50 hover:text-blue-700" },
    { icon: Youtube, href: settings.social_links.youtube, label: "YouTube", color: "hover:bg-red-100 hover:text-red-600" },
    { icon: MessageCircle, href: settings.social_links.whatsapp, label: "WhatsApp", color: "hover:bg-green-100 hover:text-green-600" },
  ];

  // Moyens de paiement (affichage)
  const paymentMethods = [
    { name: "VISA", type: "card" },
    { name: "MasterCard", type: "card" },
    { name: "MTN Mobile Money", type: "mobile" },
    { name: "Orange Money", type: "mobile" },
    { name: "Wave", type: "mobile" },
    { name: "PayPal", type: "digital" },
  ];

  // Smooth scroll to top
  const smoothScrollToTop = useCallback((duration: number = 1200) => {
    const startPosition = window.scrollY;
    if (startPosition === 0) return;
    if (scrollAnimationId.current) cancelAnimationFrame(scrollAnimationId.current);
    const startTime = performance.now();
    const animateScroll = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeProgress =
        progress < 0.5
          ? 4 * progress * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 3) / 2;
      const newPosition = startPosition * (1 - easeProgress);
      window.scrollTo(0, newPosition);
      if (progress < 1) {
        scrollAnimationId.current = requestAnimationFrame(animateScroll);
      } else {
        scrollAnimationId.current = null;
      }
    };
    scrollAnimationId.current = requestAnimationFrame(animateScroll);
  }, []);

  // Gestion affichage bouton retour haut
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      if (scrollY > 400 && !showButton) {
        setShowButton(true);
        setButtonAnimation(true);
      } else if (scrollY <= 400 && showButton) {
        setShowButton(false);
        setButtonAnimation(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [showButton]);

  useEffect(() => {
    if (buttonAnimation) {
      const timer = setTimeout(() => setButtonAnimation(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [buttonAnimation]);

  // Animation du footer à l'apparition
  useEffect(() => {
    const footer = footerRef.current;
    if (!footer) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setFooterAnimation(true);
            setTimeout(() => setFooterAnimation(false), 1000);
          }
        });
      },
      { threshold: 0.1 }
    );
    observer.observe(footer);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      {/* Bouton retour en haut */}
      {showButton && (
        <button
          onClick={() => smoothScrollToTop(1200)}
          className={`fixed bottom-16 right-2 sm:right-4 z-[49] bg-primary text-white p-2 rounded-full shadow-lg hover:bg-primary/80 transition-all hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${buttonAnimation ? "animate__animated animate__fadeInUp" : ""
            }`}
          aria-label="Retour en haut"
        >
          <ArrowUp size={18} />
        </button>
      )}

      <footer
        ref={footerRef}
        className={`relative bg-gray-900 text-white mt-20 ${footerAnimation ? "animate__animated animate__fadeInUp" : ""}`}
      >
        {/* Bandeau Newsletter (en surplomb) */}
        <div className="relative z-20 -mt-12 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] container bg-gradient-to-r from-primary to-secondary rounded-2xl shadow-xl px-5 py-4 flex flex-col lg:flex-row items-center justify-between gap-5">
          <h2 className="text-white text-base sm:text-lg md:text-xl font-bold text-center lg:text-left">
            Ne manquez aucune offre !
          </h2>
          <div className="flex flex-col sm:flex-row bg-white rounded-xl overflow-hidden w-full sm:max-w-md">
            <input
              type="email"
              placeholder="Votre adresse email"
              className="flex-1 px-4 py-2.5 outline-none text-gray-700 text-sm"
              aria-label="Email newsletter"
            />
            <button className="bg-gray-900 text-white px-5 py-2.5 font-semibold text-sm whitespace-nowrap hover:bg-gray-800 transition flex items-center justify-center gap-2">
              <Send size={16} />
              S'abonner
            </button>
          </div>
        </div>

        {/* Contenu principal du footer */}
        <div className="relative z-10 container mx-auto px-4 pt-12 pb-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-8 pb-10">
            {/* Colonne marque + description + réseaux (occupe plus de largeur) */}
            <div className="lg:col-span-2 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
                  <span className="text-primary font-bold text-2xl">K</span>
                </div>
                <div>
                  <div className="text-xl font-bold text-white">{settings.company_name}</div>
                  <div className="text-xs text-gray-400">Marketplace panafricaine</div>
                </div>
              </div>
              <p className="text-sm text-gray-300 leading-relaxed">
                {settings.short_description}
              </p>
              <div>
                <h3 className="font-semibold text-white text-sm uppercase tracking-wide mb-3">
                  Suivez-nous
                </h3>
                <div className="flex flex-wrap gap-3">
                  {socialLinks.map((social) => {
                    const Icon = social.icon;
                    return (
                      <a
                        key={social.label}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center justify-center w-9 h-9 rounded-full bg-white/10 backdrop-blur-sm transition-all duration-300 hover:scale-110 hover:rotate-3 ${social.color}`}
                        aria-label={social.label}
                      >
                        <Icon size={16} className="text-white" />
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Liens : sections du footer (version desktop) */}
            {linkSections.map((section) => (
              <div key={section.title} className="space-y-3">
                <div className="flex items-center gap-2">
                  <section.icon className="w-4 h-4 text-primary" />
                  <h3 className="font-semibold text-white text-sm uppercase tracking-wide">
                    {section.title}
                  </h3>
                </div>
                <ul className="space-y-2">
                  {section.links.map((link) => (
                    <li key={link.label}>
                      <Link
                        to={link.path}
                        className={`text-sm text-gray-300 hover:text-white transition flex items-center gap-1 group ${link.highlight ? "text-primary font-semibold" : ""
                          }`}
                      >
                        <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition" />
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Contact rapide */}
          <div className="border-t border-white/10 pt-4 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm">
            <div className="flex flex-wrap justify-center gap-4 text-gray-300">
              <a href={`mailto:${settings.email}`} className="flex items-center gap-1 hover:text-white transition">
                <Mail size={14} /> {settings.email}
              </a>
              <a href={`tel:${settings.phone.replace(/\s/g, "")}`} className="flex items-center gap-1 hover:text-white transition">
                <Phone size={14} /> {settings.phone}
              </a>
              <span className="flex items-center gap-1"><MapPin size={14} /> {settings.address}</span>
            </div>
            <div className="flex gap-4 text-xs">
              <Link to="/legal" className="hover:text-primary">Mentions légales</Link>
              <Link to="/privacy" className="hover:text-primary">Confidentialité</Link>
              <Link to="/cookies" className="hover:text-primary">Cookies</Link>
            </div>
          </div>

          {/* Copyright */}
          <div className="mt-4 pt-4 border-t border-white/10 text-center text-sm text-gray-400">
            © {currentYear} {settings.company_name}. Tous droits réservés.
          </div>
        </div>
      </footer>
    </>
  );
};

export default FooterComponent;